import SETTINGS from '@verzekeringapp-mono/shared/src/constants/settings'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import React, { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Button from '~/components/Button'
import TextInput from '~/components/TextInput'
import { useEmergencyModalContext } from '~/providers/EmergencyModalProvider'
import { getAgencyPrintout } from '@verzekeringapp-mono/shared/src/state/selectors/agency'
import { Images } from '@verzekeringapp-mono/shared/src/assets'
import LoginScreenWrapper from '~/components/screen_wrappers/login_screen_wrapper'
import useAuthentication from '@verzekeringapp-mono/shared/src/hooks/use_authentication'
import { logEvent, setUserId } from '~/utils/firebase'

const LoginScreen = () => {
  const { state } = useLocation()
  const {
    handleSubmit,
    formState: { isValid },
    control
  } = useForm({
    defaultValues: {
      email: state?.email || '',
      password: ''
    }
  })
  const { open } = useEmergencyModalContext()
  const { login } = useAuthentication()
  const navigate = useNavigate()
  const [loginError, setLoginError] = React.useState<string | null>(null)

  const [isLoading, setIsLoading] = useState(false)

  const agencyPrintout = useAppSelector(getAgencyPrintout)

  const logo = useMemo(() => {
    if (agencyPrintout?.logoUrl) {
      return `${agencyPrintout.logoUrl}?${Date.now()}`
    }

    return Images.Logo
  }, [agencyPrintout])

  const onSubmit = async (input: { email: string; password: string }) => {
    const { email, password } = input

    try {
      setIsLoading(true)
      setLoginError(null)
      const result = await login(email, password)
      const userId = result.user?.id ? `${result.user.id}` : 'unknown'

      setUserId(userId)
      logEvent('login')

      setLoginError(null)
      navigate('/dashboard')
    } catch (error) {
      setLoginError((error as Error).message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <LoginScreenWrapper>
      <img
        src={logo}
        width={180}
        height={70}
        alt={'Logo van jouw verzekeringsadviseur'}
      />

      <h1 className='title-medium mt-8'>{'Welkom'}</h1>

      <h2 className='description my-8'>
      In Mijn Polissen kun je snel schademelden en heb je toegang tot je dossier, verzekeringsbewijs, polissen en meer. Begin hier met inloggen.
      </h2>

      {loginError && (
        <div
          role='alert'
          className='my-4 rounded bg-status-danger-strong p-4 text-text-on-danger'
        >
          <span
            className='whitespace-pre-line text-text-on-danger'
            dangerouslySetInnerHTML={{ __html: loginError}}
          />
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              label='E-mailadres'
              placeholder='Je e-mailadres'
              showValidationIcon={false}
              isValid={!fieldState.invalid}
              icon={'envelope'}
              autoComplete='email'
            />
          )}
          name='email'
          control={control}
          rules={{
            required: 'Dit veld is verplicht',
            pattern: {
              value: /^(?!\.)(?!.*\.\.)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+(?<!\.)@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/,
              message: 'Dit is geen geldig e-mailadres'
            }
          }}
        />

        <React.Fragment>
          <Controller
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                label='Wachtwoord'
                placeholder='Je wachtwoord'
                type='password'
                isValid={!fieldState.invalid}
                showValidationIcon={false}
                icon={'key'}
                autoComplete='current-password'
              />
            )}
            name='password'
            control={control}
            rules={{
              required: 'Dit veld is verplicht'
            }}
          />
        </React.Fragment>

        <Button
          className={`primary-btn w-full ${isValid ? '' : 'opacity-50'} my-5`}
          type='submit'
          isLoading={isLoading}
          disabled={!isValid}
        >
          {'Inloggen'}
        </Button>
      </form>

      <div className='flex flex-col'>
        <Link
          to='/forget-password'
          className='light mt-3'
        >
        Wachtwoord vergeten?
        </Link>

        <p className='title-medium mt-6'>
        Nog geen account?
        </p>

        <p className='description mt-4'>
        Maak in een paar stappen een account aan met het e-mailadres dat bij Nh1816 Verzekeringen bekend is.
        </p>

        <Button
          className='w-full mt-3'
          onClick={() => navigate('/register')}
          variant='outline-brand'
          type='button'
        >
        Account aanmaken
        </Button>
      </div>

      <p className='mt-3'>
      In geval van nood, neem contact op via het noodnummer: <br />

        <button
          type='button'
          onClick={open}
          className='text-status-danger-foreground'
        >
          {SETTINGS.emergencyPhoneNumber}
        </button>
      </p>
    </LoginScreenWrapper>
  )
}

export default LoginScreen
